import { useCallback } from 'react'
import {
  Wallet, Coins, Shield, CheckCircle2, XCircle, Clock, Zap, DollarSign, Copy,
  type LucideIcon,
} from 'lucide-react'
import { cn, truncateAddress } from '@/lib/utils'
import { useToast } from '@/hooks/useToast'
import { useTreasuryOnchain } from '@/hooks/useTreasuryOnchain'
import { TREASURY_ADDRESS } from '@/lib/solana'
import { RealTimeIndicator } from './shared/RealTimeIndicator'

// ────────────────────────────────────────────────────────────────────────
// Inline shell component — same look as StatsCard but with full content
// control (so value color can vary per threshold without editing the
// shared StatsCard primitive).
// ────────────────────────────────────────────────────────────────────────

interface MetricShellProps {
  title: string
  icon: LucideIcon
  loading?: boolean
  children: React.ReactNode
  className?: string
}

function MetricShell({ title, icon: Icon, loading, children, className }: MetricShellProps) {
  if (loading) {
    return (
      <div className={cn('rounded-lg border bg-card p-4 sm:p-6 shadow-sm animate-pulse', className)}>
        <div className="flex items-center justify-between">
          <div className="h-4 w-24 bg-muted rounded" />
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-muted" />
        </div>
        <div className="mt-2 sm:mt-4">
          <div className="h-8 w-32 bg-muted rounded" />
        </div>
      </div>
    )
  }
  return (
    <div className={cn('rounded-lg border bg-card p-4 sm:p-6 shadow-sm', className)}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs sm:text-sm font-medium text-muted-foreground">{title}</p>
        <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-brand-fg/10 shrink-0">
          <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-brand-fg" />
        </div>
      </div>
      <div className="mt-2 sm:mt-4">{children}</div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────
// Threshold + format helpers
// ────────────────────────────────────────────────────────────────────────

function thresholdColor(
  value: number,
  thresholds: { greenAbove: number; redBelow: number }
): string {
  if (value >= thresholds.greenAbove) return 'text-status-ok'
  if (value < thresholds.redBelow) return 'text-status-error'
  return 'text-status-warning'
}

function binaryColor(matches: boolean): string {
  return matches ? 'text-status-ok' : 'text-status-error'
}

function freshnessColor(ageSeconds: number): string {
  if (ageSeconds < 30) return 'text-status-ok'
  if (ageSeconds < 120) return 'text-status-warning'
  return 'text-status-error'
}

function fmtSol(sol: number): string {
  if (sol >= 1000) return `${(sol / 1000).toFixed(2)}K`
  if (sol >= 1) return sol.toFixed(2)
  return sol.toFixed(4)
}

function fmtMicroLamports(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString('en-US')
}

// ────────────────────────────────────────────────────────────────────────
// Section component
// ────────────────────────────────────────────────────────────────────────

export function TreasuryOnchainSection() {
  const { data, isLoading } = useTreasuryOnchain()
  const { toast } = useToast()

  const copy = useCallback(
    async (text: string, label: string) => {
      try {
        await navigator.clipboard.writeText(text)
        toast({ title: `Copied ${label}`, description: text })
      } catch {
        toast({ title: 'Copy failed', variant: 'destructive' })
      }
    },
    [toast]
  )

  const treasuryAddress = TREASURY_ADDRESS.toBase58()
  const usdValue =
    data && data.pyth ? `$${(data.treasurySol * data.pyth.price).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : null

  const epoch = data?.epoch
  const epochProgress = epoch
    ? Math.min(100, Math.max(0, (epoch.slotIndex / Math.max(1, epoch.slotsInEpoch)) * 100))
    : 0

  const pythAgeSec = data?.pyth ? Math.max(0, Math.floor(Date.now() / 1000 - data.pyth.publishTime)) : null

  return (
    <div>
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
          <Wallet className="h-5 w-5 text-brand-fg" />
          Treasury & On-Chain Health
        </h3>
        <RealTimeIndicator />
      </div>

      <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        {/* Card 1 — Treasury SOL */}
        <MetricShell title="Treasury SOL" icon={Wallet} loading={isLoading}>
          <p
            className={cn(
              'font-bold truncate text-xl sm:text-3xl',
              data && thresholdColor(data.treasurySol, { greenAbove: 10, redBelow: 1 })
            )}
          >
            {data ? `${fmtSol(data.treasurySol)} SOL` : '—'}
          </p>
          <button
            type="button"
            onClick={() => copy(treasuryAddress, 'treasury address')}
            className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            title={treasuryAddress}
          >
            <span>{truncateAddress(treasuryAddress)}</span>
            <Copy className="h-3 w-3" />
            {usdValue && <span className="ml-1">· {usdValue}</span>}
          </button>
        </MetricShell>

        {/* Card 2 — Rewards Vault */}
        <MetricShell title="Rewards Vault (SOL queued for stakers)" icon={Coins} loading={isLoading}>
          <p className="font-bold truncate text-xl sm:text-3xl">
            {data ? `${fmtSol(data.rewardsVaultSol)} SOL` : '—'}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">cycles to ~0 each epoch</p>
        </MetricShell>

        {/* Card 3 — Staking Admin Wallet */}
        <MetricShell title="Staking Admin Wallet" icon={Shield} loading={isLoading}>
          <p
            className={cn(
              'font-bold truncate text-xl sm:text-3xl',
              data && data.stakingAdmin.admin
                ? thresholdColor(data.stakingAdmin.balanceSol, { greenAbove: 0.5, redBelow: 0.5 })
                : ''
            )}
          >
            {data && data.stakingAdmin.admin ? `${fmtSol(data.stakingAdmin.balanceSol)} SOL` : '—'}
          </p>
          {data?.stakingAdmin.admin ? (
            <button
              type="button"
              onClick={() => copy(data.stakingAdmin.admin!, 'admin address')}
              className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              title={data.stakingAdmin.admin}
            >
              <span>{truncateAddress(data.stakingAdmin.admin)}</span>
              <Copy className="h-3 w-3" />
            </button>
          ) : (
            <p className="text-xs text-muted-foreground mt-0.5">unable to read admin address</p>
          )}
        </MetricShell>

        {/* Card 4 — DBC config integrity */}
        <MetricShell
          title="DBC Config Integrity"
          icon={data?.dbcConfig.matches ? CheckCircle2 : XCircle}
          loading={isLoading}
        >
          {data ? (
            <>
              <div className={cn('flex items-center gap-2 font-semibold', binaryColor(data.dbcConfig.matches))}>
                {data.dbcConfig.matches ? (
                  <CheckCircle2 className="h-6 w-6" />
                ) : (
                  <XCircle className="h-6 w-6" />
                )}
                <span className="text-sm sm:text-base">
                  {data.dbcConfig.matches ? 'Matches treasury' : 'MISMATCH — investigate'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                checked {Math.max(0, Math.floor((Date.now() - data.dbcConfig.checkedAt) / 1000))}s ago
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">—</p>
          )}
        </MetricShell>

        {/* Card 5 — Solana Epoch */}
        <MetricShell title={epoch ? `Solana Epoch ${epoch.epoch}` : 'Solana Epoch'} icon={Clock} loading={isLoading}>
          {epoch ? (
            <>
              <p className="font-bold text-xl sm:text-3xl">{epochProgress.toFixed(1)}%</p>
              <div className="mt-2 h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-dextoro transition-all"
                  style={{ width: `${epochProgress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {epoch.slotIndex.toLocaleString('en-US')} / {epoch.slotsInEpoch.toLocaleString('en-US')}
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">—</p>
          )}
        </MetricShell>

        {/* Card 6 — Network Priority Fee */}
        <MetricShell title="Network Priority Fee" icon={Zap} loading={isLoading}>
          {(() => {
            // 4 cases so operators can distinguish "RPC failed" from "network quiet" from "live fees".
            if (!data?.priorityFee) {
              return <p className="text-sm text-muted-foreground">—</p>
            }
            const { p50, p95, sampleSize } = data.priorityFee
            if (p50 === 0 && p95 === 0 && sampleSize === 0) {
              return (
                <>
                  <p className="text-sm text-muted-foreground">—</p>
                  <p className="text-xs text-muted-foreground mt-0.5">No recent samples</p>
                </>
              )
            }
            if (p50 === 0 && p95 === 0) {
              return (
                <>
                  <p className="font-bold text-xl sm:text-3xl truncate">~0</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Network quiet · {sampleSize} samples
                  </p>
                </>
              )
            }
            return (
              <>
                <p className="font-bold text-xl sm:text-3xl truncate">
                  {fmtMicroLamports(p50)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  p50 µLam/CU · p95 {fmtMicroLamports(p95)}
                </p>
              </>
            )
          })()}
        </MetricShell>

        {/* Card 7 — SOL/USD Pyth Hermes */}
        <MetricShell title="SOL/USD — Pyth Hermes" icon={DollarSign} loading={isLoading}>
          {data?.pyth && pythAgeSec !== null ? (
            <>
              <p className="font-bold text-xl sm:text-3xl truncate">
                ${data.pyth.price.toLocaleString('en-US', { maximumFractionDigits: 2 })}
              </p>
              <p className={cn('text-xs mt-0.5', freshnessColor(pythAgeSec))}>
                {pythAgeSec}s ago
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">—</p>
          )}
        </MetricShell>
      </div>
    </div>
  )
}
